import React, { useState, useEffect, useRef } from 'react';

function AnimatedInsightCard({ label, value, valueClass, classes, index }) {
    const [isVisible, setIsVisible] = useState(false);
    const [animatedValue, setAnimatedValue] = useState('');
    const cardRef = useRef(null);

    // Intersection Observer to detect when card is in viewport
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            {
                threshold: 0.2, // Trigger when 20% of the card is visible
                rootMargin: '0px'
            }
        );

        if (cardRef.current) {
            observer.observe(cardRef.current);
        }

        return () => {
            if (cardRef.current) {
                observer.unobserve(cardRef.current);
            }
        };
    }, []);

    // Animate value when card becomes visible
    useEffect(() => {
        if (!isVisible || !value) return;

        // Check if value contains a number
        const numberMatch = value.toString().match(/[\d,]+\.?\d*/);

        if (numberMatch) {
            const numericPart = parseFloat(numberMatch[0].replace(/,/g, ''));
            const prefix = value.toString().substring(0, value.toString().indexOf(numberMatch[0]));
            const suffix = value.toString().substring(value.toString().indexOf(numberMatch[0]) + numberMatch[0].length);

            if (!isNaN(numericPart)) {
                const duration = 1500; // 1.5 seconds
                const steps = 60;
                const increment = numericPart / steps;
                const stepDuration = duration / steps;
                let currentStep = 0;

                const timer = setInterval(() => {
                    currentStep++;
                    if (currentStep >= steps) {
                        setAnimatedValue(value);
                        clearInterval(timer);
                    } else {
                        const currentValue = Math.floor(increment * currentStep);
                        setAnimatedValue(`${prefix}${currentValue.toLocaleString()}${suffix}`);
                    }
                }, stepDuration);

                return () => clearInterval(timer);
            } else {
                setAnimatedValue(value);
            }
        } else {
            // If no number found, just show the value directly
            setAnimatedValue(value);
        }
    }, [isVisible, value]);

    return (
        <div
            ref={cardRef}
            className={classes.insightItem}
            style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: `all 0.6s ease ${index * 0.1}s`
            }}
        >
            <div
                className={classes.insightLabel}
                style={{
                    fontSize: '0.95rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    fontWeight: 600
                }}
            >
                {label}
            </div>
            <div
                className={`${classes.insightValue} ${valueClass}`}
                style={{
                    fontSize: '2.2rem',
                    fontWeight: 'bold',
                    lineHeight: 1.3,
                    marginTop: '10px'
                }}
            >
                {animatedValue || value}
            </div>
        </div>
    );
}

export default AnimatedInsightCard;